import React, { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";

export default function ScrollProgressWidget() {
  const [scrollPercent, setScrollPercent] = useState(0);
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = (document.documentElement.scrollHeight || document.body.scrollHeight) - window.innerHeight;
      const pct = docHeight > 0 ? Math.min(100, Math.max(0, Math.round((scrollTop / docHeight) * 100))) : 0;
      setScrollPercent(pct);
      setVisible(scrollTop > 60);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const radius = 22;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (scrollPercent / 100) * circumference;

  return (
    <div
      onClick={scrollToTop}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`fixed bottom-6 right-6 z-[9999] cursor-pointer select-none transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"
      }`}
      title="Scroll to Top"
      data-testid="scroll-progress-widget"
    >
      <div className="relative w-14 h-14 rounded-full bg-[#070b12]/95 border border-white/10 hover:border-lime-400/50 shadow-[0_10px_30px_rgba(0,0,0,0.85)] backdrop-blur-md flex items-center justify-center group transition-transform duration-200 hover:scale-110">
        
        {/* SVG Circular Progress Ring */}
        <svg className="w-full h-full -rotate-90 transform" viewBox="0 0 52 52">
          {/* Background Track */}
          <circle
            cx="26"
            cy="26"
            r={radius}
            className="text-white/10"
            strokeWidth="3"
            stroke="currentColor"
            fill="transparent"
          />
          {/* Active Progress Stroke with Glow */}
          <circle
            cx="26"
            cy="26"
            r={radius}
            className="text-[#a3e635] transition-all duration-100"
            strokeWidth="3"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            stroke="currentColor"
            fill="transparent"
            style={{
              filter: "drop-shadow(0 0 5px rgba(163, 230, 53, 0.85))"
            }}
          />
        </svg>

        {/* Center Percentage or Arrow Up on Hover */}
        <div className="absolute inset-0 flex items-center justify-center">
          {hovered ? (
            <ChevronUp className="w-5 h-5 text-[#a3e635] animate-bounce" />
          ) : (
            <span className="font-chakra font-black text-xs text-white tracking-tight">
              {scrollPercent}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
